import React, { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';

const AdminLogin: React.FC = () => {
  const { isAdminLoggedIn, loginAdmin } = useAdmin();
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (isAdminLoggedIn) {
    return <Navigate to="/admin" replace />;
  }

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const success = await loginAdmin(formData.username, formData.password);
      if (!success) {
        setError('Invalid username or password.');
      }
    } catch (err) {
      setError('An error occurred during login.');
    } finally {
      setIsLoading(false);
    }
  };

  const isDev =
    process.env.REACT_APP_ENABLE_DEV_LOGIN === 'true' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  return (
    <div className="min-h-screen bg-cream-50 font-sans flex flex-col relative overflow-hidden">
      {/* Background — subtle botanical atmosphere */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.35]"
        style={{
          backgroundImage: `
            radial-gradient(ellipse 800px 600px at 85% -10%, rgba(197, 165, 114, 0.18), transparent 60%),
            radial-gradient(ellipse 700px 500px at 15% 110%, rgba(45, 89, 64, 0.10), transparent 60%)
          `,
        }}
      />

      {/* Top bar — just the brand, no nav */}
      <header className="relative z-10 h-[52px] flex items-center justify-between px-6 sm:px-8 border-b border-warm-200/40">
        <Link to="/" className="flex items-baseline gap-0 group">
          <span className="font-display font-bold tracking-[-0.04em] text-forest-800 text-[20px] group-hover:text-forest-600 transition-colors">
            Libera
          </span>
          <span className="font-display font-bold tracking-[-0.04em] text-gold-400 text-[20px]">
            Cure
          </span>
          <span className="ml-2 text-[9px] font-semibold text-warm-400 uppercase tracking-[0.14em]">
            Admin
          </span>
        </Link>

        <Link
          to="/"
          className="text-[11.5px] font-medium text-warm-500 hover:text-forest-800 transition-colors inline-flex items-center gap-1.5"
        >
          <svg className="w-[13px] h-[13px]" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back to site
        </Link>
      </header>

      {/* Main card */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-[400px]">
          {/* Heading */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-forest-50/80 border border-forest-100 mb-5">
              <span className="w-1 h-1 rounded-full bg-gold-400" />
              <span className="text-[10px] font-semibold text-forest-700 uppercase tracking-[0.14em]">
                Restricted Area
              </span>
            </div>
            <h1 className="font-display text-[34px] leading-[1.0] tracking-[-0.025em] text-forest-900 mb-2">
              Sign in to the <span className="text-gold-400 italic font-medium">console</span>
            </h1>
            <p className="text-[12.5px] text-warm-500">
              Authorized personnel only · activity is logged
            </p>
          </div>

          {/* Form card */}
          <div className="bg-cream-50/90 backdrop-blur-xl border border-warm-200/70 rounded-2xl shadow-[0_2px_40px_-10px_rgba(45,89,64,0.10)] p-7">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Error */}
              {error && (
                <div className="flex items-start gap-2.5 px-3.5 py-2.5 bg-rose-50/80 border border-rose-200/70 rounded-lg">
                  <svg className="w-[14px] h-[14px] text-rose-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                  </svg>
                  <span className="text-[12px] font-medium text-rose-700 leading-snug">{error}</span>
                </div>
              )}

              {/* Username */}
              <div>
                <label htmlFor="username" className="block text-[10px] font-semibold text-warm-500 uppercase tracking-[0.12em] mb-1.5">
                  Username
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  autoComplete="username"
                  value={formData.username}
                  onChange={(e) => handleInputChange('username', e.target.value)}
                  placeholder="admin"
                  className="w-full px-3.5 py-2.5 text-[13.5px] text-forest-900 placeholder-warm-300 bg-cream-50 border border-warm-200 rounded-lg focus:outline-none focus:border-forest-400 focus:ring-2 focus:ring-forest-100 transition-all duration-200"
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-[10px] font-semibold text-warm-500 uppercase tracking-[0.12em] mb-1.5">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-3.5 py-2.5 text-[13.5px] text-forest-900 placeholder-warm-300 bg-cream-50 border border-warm-200 rounded-lg focus:outline-none focus:border-forest-400 focus:ring-2 focus:ring-forest-100 transition-all duration-200"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-[13px] font-semibold text-cream-50 bg-forest-800 hover:bg-forest-900 rounded-lg transition-all duration-300 ease-spring disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_16px_-4px_rgba(20,43,33,0.3)]"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v2a6 6 0 00-6 6H4z"></path>
                    </svg>
                    <span>Verifying</span>
                  </>
                ) : (
                  <>
                    <span>Sign in</span>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                    </svg>
                  </>
                )}
              </button>
            </form>

            {/* Divider + security note */}
            <div className="mt-6 pt-5 border-t border-warm-200/60">
              <div className="flex items-start gap-2 text-[10.5px] text-warm-400 leading-relaxed">
                <svg className="w-[12px] h-[12px] flex-shrink-0 mt-0.5 text-warm-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
                <span>
                  Credentials are verified against Supabase. All access attempts are logged and monitored for anomalies.
                </span>
              </div>
            </div>
          </div>

          {/* Dev account panel */}
          {isDev && (
            <div className="mt-4 px-4 py-3 bg-gold-50/80 border border-gold-200/70 rounded-xl">
              <div className="flex items-center gap-2 mb-1.5">
                <svg className="w-[12px] h-[12px] text-gold-600" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                </svg>
                <span className="text-[10px] font-bold text-gold-700 uppercase tracking-[0.12em]">
                  Dev Environment
                </span>
              </div>
              <div className="text-[11px] text-gold-800 space-y-0.5 font-mono">
                <div>email: <span className="text-gold-900">{process.env.REACT_APP_DEV_ADMIN_EMAIL || '—'}</span></div>
                <div>pass: <span className="text-gold-900">{process.env.REACT_APP_DEV_ADMIN_PASSWORD || '—'}</span></div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 h-10 flex items-center justify-center border-t border-warm-200/40">
        <p className="text-[10px] text-warm-400 tracking-wide">
          © {new Date().getFullYear()} LiberaCure · Internal Tool
        </p>
      </footer>
    </div>
  );
};

export default AdminLogin;
